import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, Chip, Divider
} from '@mui/material';
import { crearTramitePuam, getChecklistFormularios, getTramitesPuam } from '../../services/puamService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

/**
 * Formulario para iniciar un trámite del área PUAM. A diferencia de Jubilación, PUAM es plano:
 * sin subtipo/régimen/empleadores. El checklist es siempre el mismo (5 formularios fijos).
 */
export default function FormularioIniciarTramitePuam({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        organismo: '',
        trabajaRelacionDependencia: false,
        tieneMonotributoActivo: false,
        mtbDadoDeBaja: false,
        controlSocioeconomicoRealizado: false,
        cobraOtroBeneficio: false,
        comentarios: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de PUAM duplicado para el mismo cliente (patrón RTI/ART/Jubilación) ---
    const [tieneDuplicadoPuam, setTieneDuplicadoPuam] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Checklist (siempre los mismos 5 formularios: se trae una sola vez) ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(true);

    // Advertencia de viabilidad NO bloqueante (decisión 2 del plan): no impide crear el trámite.
    const hayIncompatibilidad = form.trabajaRelacionDependencia
        || (form.tieneMonotributoActivo && !form.mtbDadoDeBaja)
        || form.cobraOtroBeneficio;

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Preview del checklist: PUAM no tiene condicionales, se trae una sola vez.
    useEffect(() => {
        const cargarChecklist = async () => {
            setCargandoChecklist(true);
            try {
                const data = await getChecklistFormularios();
                setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                setChecklist([]);
            } finally {
                setCargandoChecklist(false);
            }
        };
        cargarChecklist();
    }, []);

    // Trámite de PUAM ya existente para el cliente seleccionado (patrón RTI/ART/Jubilación).
    // PUAM no tiene estados de cierre, así que "activo" = existe cualquier trámite con ese CUIL.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoPuam(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesPuam(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicadoPuam(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de PUAM duplicados:', err);
                if (activo) {
                    setTieneDuplicadoPuam(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked, ...(name === 'tieneMonotributoActivo' && !checked ? { mtbDadoDeBaja: false } : {}) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (tieneDuplicadoPuam && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de PUAM para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                organismo: form.organismo || null,
                trabajaRelacionDependencia: form.trabajaRelacionDependencia,
                tieneMonotributoActivo: form.tieneMonotributoActivo,
                mtbDadoDeBaja: form.tieneMonotributoActivo ? form.mtbDadoDeBaja : false,
                controlSocioeconomicoRealizado: form.controlSocioeconomicoRealizado,
                cobraOtroBeneficio: form.cobraOtroBeneficio,
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
            };
            const creado = await crearTramitePuam(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de PUAM creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/puam/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="person-vcard"
                        titulo="Iniciar Trámite de PUAM"
                        descripcion="Complete los datos para registrar un nuevo trámite previsional"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-puam"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {/* Datos del trámite */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}

                            <TextField select label="Organismo" name="organismo" value={form.organismo} onChange={handleChange} fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES.">
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                            </TextField>

                            <FormControlLabel
                                control={<Checkbox name="trabajaRelacionDependencia" checked={form.trabajaRelacionDependencia} onChange={handleCheck} />}
                                label="Trabaja en relación de dependencia"
                            />
                            <FormControlLabel
                                control={<Checkbox name="tieneMonotributoActivo" checked={form.tieneMonotributoActivo} onChange={handleCheck} />}
                                label="Tiene monotributo activo"
                            />
                            {form.tieneMonotributoActivo && (
                                <FormControlLabel
                                    sx={{ ml: 2 }}
                                    control={<Checkbox name="mtbDadoDeBaja" checked={form.mtbDadoDeBaja} onChange={handleCheck} />}
                                    label="El monotributo ya fue dado de baja"
                                />
                            )}
                            <FormControlLabel
                                control={<Checkbox name="controlSocioeconomicoRealizado" checked={form.controlSocioeconomicoRealizado} onChange={handleCheck} />}
                                label="Control socioeconómico realizado"
                            />
                            <FormControlLabel
                                control={<Checkbox name="cobraOtroBeneficio" checked={form.cobraOtroBeneficio} onChange={handleCheck} />}
                                label="Cobra otro beneficio previsional"
                            />

                            {/* Advertencia de viabilidad — no bloqueante, el operador decide */}
                            {hayIncompatibilidad && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Condición incompatible con PUAM — revisar / dar de baja el monotributo / verificar el otro beneficio.
                                    No impide iniciar el trámite; quedará registrado para su seguimiento.
                                </Alert>
                            )}

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Observaciones libres sobre el trámite." />

                            {/* Checklist (siempre los mismos 5 formularios) */}
                            <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                    <i className="bi bi-check2-square me-2"></i>
                                    Formularios requeridos {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {checklist.length === 0 && !cargandoChecklist ? (
                                    <Typography variant="body2" color="text.secondary">Sin formularios configurados.</Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {checklist.map(f => <Chip key={f.codigo} label={f.nombre} size="small" color="primary" variant="outlined" />)}
                                    </Stack>
                                )}
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicadoPuam && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de PUAM' : `${cantidadDuplicados} trámites de PUAM`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicadoPuam && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de PUAM igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de PUAM activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
